import { useEffect, useState } from "react";
import type { EnvAdvancedConfig, SceneState, Vec3 } from "../../../../shared/api-contract";
import { useDomainState, useEditorStore, useMutate } from "../../../../state/editor/useEditor";
import { useViewer } from "../../../../state/ViewerProvider";
import { CompactCheck, CompactColor, CompactSlider, CompactText, MiniBtn, Row } from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";

const ENV1_ROTATION_INERT: InertTag = {
  reason:
    "Stored, never read. setEnvAdvanced acts only on fixedDirection and envmapAsBackground, and \u201cApply Environment1\u201d calls loadEnvFromUrl without it. Use \u201cEnv Rotation\u201d above.",
};
import { pickFile } from "../../../../util/download";

/** Which long-running load owns the panel's single pending slot. */
type Job = "image" | "env" | "env1" | "settings";

const msgOf = (e: unknown) => (e instanceof Error && e.message) || "Failed — see console.";

/**
 * Background / Environment (advanced) — iJewel SimpleBackgroundEnvUiPlugin1.
 * The shared fields reuse the existing scene env/background RPCs (mirroring
 * iJewel's overlap between the basic + advanced panels); the genuinely-new bits
 * are the secondary env slot (Environment1), Fixed Env Direction and Cache.
 */
export function EnvAdvancedControl() {
  const { client } = useViewer();
  const r = client.ring;
  // "Fixed Env Direction" is the boolean face of the Scene tab's "Env Follow
  // Camera" slider, and the left-rail Advanced panel shows the same checkbox.
  // All three read and write the shared `scene` slice so they stay in lockstep;
  // a panel-local copy seeded once at mount is what let them disagree. (The
  // slice is undefined until the store's eager seed lands; the engine boots
  // world-locked, so 0 is the honest stand-in.)
  const scene = useDomainState("scene") as SceneState | undefined;
  const fixedEnv = (scene?.envFollowCamera ?? 0) === 0;
  const mutate = useMutate();
  const store = useEditorStore();
  /**
   * Every background writer below picks a new background SOURCE in the viewer
   * (a colour drops an env backdrop, an image drops the colour, …), and this
   * panel writes them straight over RPC with no optimistic patch. Re-read the
   * scene slice or the Scene tab's own rows — "Show Env as Background" and the
   * Background Blur slider, which is enabled only for a blurrable source — keep
   * describing a background that is no longer on screen.
   */
  const resyncScene = () => void store.refresh("scene");
  const [adv, setAdv] = useState<EnvAdvancedConfig | null>(null);
  const [bg, setBg] = useState<string>("#ffffff");
  const [bgIntensity, setBgIntensity] = useState(1);
  const [envIntensity, setEnvIntensity] = useState(1);
  // The whole vector, not just Y: the slider writes all three components back,
  // and hard-coding X/Z to 0 silently wiped whatever the Environment section's
  // own env-rotation row had set.
  const [envRot, setEnvRot] = useState<Vec3>({ x: 0, y: 0, z: 0 });
  const [pending, setPending] = useState<Job | null>(null);
  const [err, setErr] = useState<{ job: Job; message: string } | null>(null);
  const [mountErr, setMountErr] = useState<string | null>(null);

  useEffect(() => {
    r.getEnvAdvanced().then(setAdv).catch((e: unknown) => setMountErr(msgOf(e)));
    r.getSceneState().then((s: SceneState) => {
      if (s.background) setBg(s.background);
      if (s.backgroundIntensity != null) setBgIntensity(s.backgroundIntensity);
      if (s.envIntensity != null) setEnvIntensity(s.envIntensity);
      // Env Rotation used to be left at its useState(0) default, so it read 0
      // on every mount while the scene was actually rotated — and the first
      // drag then snapped the environment to that phantom value.
      if (s.envRotation) setEnvRot(s.envRotation);
    }).catch((e: unknown) => setErr({ job: "settings", message: msgOf(e) }));
  }, [r]);

  const setA = (p: Partial<EnvAdvancedConfig>) => {
    setErr(null);
    return r
      .setEnvAdvanced(p)
      .then((next) => {
        setAdv(next);
        // envmapAsBackground / fixedDirection write through to the scene
        // (setShowEnvAsBackground / setEnvFollowCamera), so the slice has moved.
        if (p.envmapAsBackground != null || p.fixedDirection != null) resyncScene();
      })
      .catch((e: unknown) => setErr({ job: "settings", message: msgOf(e) }));
  };

  /**
   * Hold one pending slot for the whole duration of an env load. Decoding an
   * HDR/EXR (or uploading a background image) takes seconds inside the viewer;
   * previously the buttons stayed enabled and each extra click stacked another
   * concurrent decode, and a rejected RPC vanished into a bare `void`.
   */
  const run = async (job: Job, work: () => Promise<unknown>) => {
    if (pending) return;
    setPending(job);
    setErr(null);
    try {
      await work();
    } catch (e) {
      setErr({ job, message: msgOf(e) });
    } finally {
      setPending(null);
    }
  };

  const runPick = (job: Job, accept: string, load: (f: File) => Promise<unknown>) =>
    run(job, async () => {
      // pickFile resolves only once `onFile` settles, but it swallows an
      // `onFile` rejection (it would otherwise surface as an unhandled
      // rejection), so carry the failure out by hand to show it inline.
      let failure: unknown;
      await pickFile(accept, async (f) => {
        try {
          await load(f);
        } catch (e) {
          failure = e;
        }
      });
      if (failure) throw failure;
    });

  const errFor = (job: Job) =>
    err && err.job === job ? <div className="px-3 pb-1.5 text-[11px] text-ed-red">{err.message}</div> : null;

  if (!adv) {
    // A failed getEnvAdvanced used to render nothing at all, so the section
    // looked empty rather than broken.
    return mountErr ? <div className="px-3 py-1.5 text-[11px] text-ed-red">{mountErr}</div> : null;
  }
  return (
    <>
      <Row label="Color"><CompactColor value={bg} onChange={(v) => { setBg(v); void r.setBackground(v).then(resyncScene); }} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="image"
          busy={pending === "image"}
          disabled={pending !== null}
          onClick={() => void runPick("image", ".jpg,.png,.webp,.svg", (f) => r.setBackgroundImageFile(f).then(resyncScene))}
        >
          Image
        </MiniBtn>
        <MiniBtn icon="hide_image" onClick={() => void r.setTransparentBackground().then(resyncScene)}>Set Transparent BG</MiniBtn>
      </div>
      {errFor("image")}
      <Row label="EnvMap BG"><CompactCheck checked={adv.envmapAsBackground} onChange={(v) => void setA({ envmapAsBackground: v })} /></Row>
      <Row label="BG Intensity"><CompactSlider value={bgIntensity} min={0} max={16} step={0.01} onChange={(v) => { setBgIntensity(v); void r.setBackgroundIntensity(v); }} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="panorama_photosphere"
          busy={pending === "env"}
          disabled={pending !== null}
          onClick={() => void runPick("env", ".hdr,.exr,.jpg,.png", (f) => r.loadEnvFromFile(f))}
        >
          Environment
        </MiniBtn>
      </div>
      {errFor("env")}
      <Row label="Env Rotation">
        {/* Same range as Background & environment › Env Rotation (−π..π):
            0..2π here clamped every negative angle set there to 0. */}
        <CompactSlider
          value={envRot.y}
          min={-3.1416}
          max={3.1416}
          step={0.01}
          onChange={(v) => { setEnvRot({ ...envRot, y: v }); void r.setEnvRotation(envRot.x, v, envRot.z); }}
        />
      </Row>
      <Row label="Env Intensity"><CompactSlider value={envIntensity} min={0} max={5} step={0.01} onChange={(v) => { setEnvIntensity(v); void r.setEnvIntensity(v); }} /></Row>
      <Row label="Environment1"><CompactText value={adv.env1Url} placeholder="HDR URL" onChange={(v) => void setA({ env1Url: v })} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="filter_2"
          busy={pending === "env1"}
          // Without a URL the click was a silent no-op; disabling it says so.
          disabled={pending !== null || !adv.env1Url}
          onClick={() => void run("env1", () => r.loadEnvFromUrl(adv.env1Url))}
        >
          Apply Environment1
        </MiniBtn>
      </div>
      {adv.env1Url ? null : <div className="px-3 pb-1.5 text-[11px] text-ed-text-3">Enter an HDR URL above to apply it.</div>}
      {errFor("env1")}
      <Row label="Env1 Rotation" inert={ENV1_ROTATION_INERT}><CompactSlider value={adv.env1Rotation} min={0} max={6.283} step={0.01} onChange={(v) => void setA({ env1Rotation: v })} /></Row>
      <Row label="Fixed Env Direction">
        <CompactCheck
          checked={fixedEnv}
          onChange={(v) => {
            const follow = v ? 0 : 1;
            mutate("scene", {
              patch: { envFollowCamera: follow },
              call: (api) => Promise.resolve(api.setEnvFollowCamera(follow)),
            });
          }}
        />
      </Row>
      {errFor("settings")}
    </>
  );
}
